import { useCallback, useEffect, useRef, useState } from "react"

interface UseCronometro {
  /** Segundos restantes (inteiro, >= 0). null enquanto não inicializado. */
  restante: number | null
  /** Ressincroniza com o valor autoritativo do servidor. */
  sincronizar: (segundosDoServidor: number) => void
}

/**
 * Cronômetro regressivo por deadline absoluto: um setInterval que decrementa
 * congela em aba de background e o relógio da tela mente. Aqui cada tick
 * recalcula contra Date.now(), então voltar para a aba corrige na hora.
 * Dispara aoZerar exatamente uma vez.
 */
export function useCronometro(segundosIniciais: number | null, aoZerar: () => void): UseCronometro {
  const deadlineRef = useRef<number | null>(null)
  const zerouRef = useRef(false)
  const aoZerarRef = useRef(aoZerar)
  aoZerarRef.current = aoZerar

  const [restante, setRestante] = useState<number | null>(
    segundosIniciais === null ? null : Math.max(0, Math.floor(segundosIniciais))
  )

  const sincronizar = useCallback((segundosDoServidor: number) => {
    deadlineRef.current = Date.now() + segundosDoServidor * 1000
    setRestante(Math.max(0, Math.floor(segundosDoServidor)))
  }, [])

  useEffect(() => {
    if (segundosIniciais === null || deadlineRef.current !== null) return
    deadlineRef.current = Date.now() + segundosIniciais * 1000
    setRestante(Math.max(0, Math.floor(segundosIniciais)))
  }, [segundosIniciais])

  useEffect(() => {
    const id = window.setInterval(() => {
      if (deadlineRef.current === null) return
      const segundos = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000))
      setRestante(segundos)
      if (segundos === 0 && !zerouRef.current) {
        zerouRef.current = true
        aoZerarRef.current()
      }
    }, 250)
    return () => window.clearInterval(id)
  }, [])

  return { restante, sincronizar }
}
